import React, { useState, useEffect } from "react";
import { useApp } from "../../context/AppContext";
import { CalendarDays, Clock, CheckCircle, XCircle, AlertCircle, Plus, UserCheck, Coffee } from "lucide-react";

const ATTENDANCE_KEY = "fb11_attendance";
const LEAVE_KEY = "fb11_leaves";

function getAttendance() {
  try { return JSON.parse(localStorage.getItem(ATTENDANCE_KEY) || "[]"); } catch { return []; }
}
function saveAttendance(data) { localStorage.setItem(ATTENDANCE_KEY, JSON.stringify(data)); }
function getLeaves() {
  try { return JSON.parse(localStorage.getItem(LEAVE_KEY) || "[]"); } catch { return []; }
}
function saveLeaves(data) { localStorage.setItem(LEAVE_KEY, JSON.stringify(data)); }

export default function Attendance() {
  const { team, mode, currentUserId } = useApp();
  const [attendance, setAttendance] = useState(getAttendance());
  const [leaves, setLeaves] = useState(getLeaves());
  const [tab, setTab] = useState("attendance");
  const [showLeaveForm, setShowLeaveForm] = useState(false);
  const [leaveForm, setLeaveForm] = useState({ type: "Sick Leave", from: "", to: "", reason: "" });
  const [msg, setMsg] = useState("");

  useEffect(() => { saveAttendance(attendance); }, [attendance]);
  useEffect(() => { saveLeaves(leaves); }, [leaves]);

  const employees = (team || []).filter(m => m.role === "employee" || m.salary);
  const today = new Date().toISOString().slice(0, 10);
  const currentMonth = new Date().toISOString().slice(0, 7);

  const currentEmp = mode === "employee" ? employees.find(e =>
    e.id === currentUserId || e.code === currentUserId || e.employeeCode === currentUserId || e.name === currentUserId
  ) : null;

  const getEmpId = (emp) => emp.code || emp.employeeCode || emp.id || emp.name;

  // Mark attendance
  const markAttendance = (empId, status) => {
    const existing = attendance.find(a => a.empId === empId && a.date === today);
    if (existing) {
      setAttendance(attendance.map(a => a.empId === empId && a.date === today ? { ...a, status, time: new Date().toLocaleTimeString() } : a));
    } else {
      setAttendance([...attendance, { empId, date: today, status, time: new Date().toLocaleTimeString(), markedBy: mode }]);
    }
    setMsg(status === "present" ? "✓ Marked Present" : status === "absent" ? "✗ Marked Absent" : "☕ Marked Half Day");
    setTimeout(() => setMsg(""), 2000);
  };

  // Get attendance stats for an employee
  const getStats = (empId) => {
    const monthRecords = attendance.filter(a => a.empId === empId && a.date.startsWith(currentMonth));
    const present = monthRecords.filter(a => a.status === "present").length;
    const absent = monthRecords.filter(a => a.status === "absent").length;
    const halfDay = monthRecords.filter(a => a.status === "halfday").length;
    const workingDays = present + absent + halfDay;
    const percentage = workingDays > 0 ? Math.round((present + halfDay * 0.5) / workingDays * 100) : 0;
    return { present, absent, halfDay, workingDays, percentage };
  };

  // Today's status
  const getTodayStatus = (empId) => {
    const rec = attendance.find(a => a.empId === empId && a.date === today);
    return rec ? rec.status : null;
  };

  // Submit leave request
  const submitLeave = () => {
    if (!leaveForm.from || !leaveForm.to || !leaveForm.reason) {
      setMsg("Fill all fields"); return;
    }
    const empId = currentEmp ? getEmpId(currentEmp) : currentUserId;
    const empName = currentEmp ? currentEmp.name : currentUserId;
    const newLeave = {
      id: "LV" + Date.now(),
      empId, empName,
      type: leaveForm.type,
      from: leaveForm.from,
      to: leaveForm.to,
      reason: leaveForm.reason,
      status: "Pending",
      appliedOn: today
    };
    setLeaves([...leaves, newLeave]);
    setShowLeaveForm(false);
    setLeaveForm({ type: "Sick Leave", from: "", to: "", reason: "" });
    setMsg("Leave request submitted! ✓");
    setTimeout(() => setMsg(""), 3000);
  };

  // Approve/Reject leave (admin)
  const updateLeave = (id, status) => {
    setLeaves(leaves.map(l => l.id === id ? { ...l, status } : l));
    setMsg(status === "Approved" ? "Leave Approved ✓" : "Leave Rejected ✗");
    setTimeout(() => setMsg(""), 2000);
  };

  // Count days between dates
  const daysBetween = (from, to) => {
    const d1 = new Date(from);
    const d2 = new Date(to);
    return Math.ceil((d2 - d1) / (1000 * 60 * 60 * 24)) + 1;
  };

  const cardStyle = { padding: 24, borderRadius: 16, background: "rgba(15,23,42,.6)", border: "1px solid rgba(100,116,139,.15)", marginBottom: 20 };
  const badgeStyle = (color) => ({ padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700, background: `rgba(${color},.1)`, color: `rgb(${color})`, border: `1px solid rgba(${color},.2)` });

  return (
    <div style={{ padding: "24px 32px", maxWidth: 1000, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: "#E2E8F0", margin: 0 }}>
            <CalendarDays size={22} style={{ verticalAlign: "middle", marginRight: 10, color: "#3B82F6" }} />
            Attendance & Leave
          </h2>
          <p style={{ color: "#64748B", fontSize: 13, margin: "4px 0 0" }}>
            {mode === "admin" ? "Admin > Attendance" : "Employee > My Attendance"}
          </p>
        </div>
        {mode === "employee" && (
          <button onClick={() => setShowLeaveForm(true)} style={{ padding: "10px 20px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#3B82F6,#2563EB)", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
            <Plus size={16} /> Apply Leave
          </button>
        )}
      </div>

      {msg && <div style={{ padding: "10px 16px", borderRadius: 10, marginBottom: 16, fontSize: 13, fontWeight: 600, background: msg.includes("✓") ? "rgba(16,185,129,.1)" : "rgba(239,68,68,.1)", color: msg.includes("✓") ? "#10B981" : "#EF4444", border: `1px solid ${msg.includes("✓") ? "rgba(16,185,129,.2)" : "rgba(239,68,68,.2)"}` }}>{msg}</div>}

      {/* Tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        {["attendance", "leaves"].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: tab === t ? "rgba(59,130,246,.15)" : "rgba(100,116,139,.08)", color: tab === t ? "#3B82F6" : "#64748B", fontSize: 13, fontWeight: 600, cursor: "pointer", textTransform: "capitalize" }}>
            {t === "attendance" ? "📋 Attendance" : "🏖️ Leave Requests"}
          </button>
        ))}
      </div>

      {/* ====== ATTENDANCE TAB ====== */}
      {tab === "attendance" && (
        <>
          {/* Stats Cards */}
          {(mode === "employee" && currentEmp) && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 20 }}>
              {(() => {
                const stats = getStats(getEmpId(currentEmp));
                return [
                  { label: "Present", value: stats.present, icon: <CheckCircle size={20} />, color: "#10B981" },
                  { label: "Absent", value: stats.absent, icon: <XCircle size={20} />, color: "#EF4444" },
                  { label: "Half Day", value: stats.halfDay, icon: <Coffee size={20} />, color: "#F59E0B" },
                  { label: "Attendance %", value: stats.percentage + "%", icon: <UserCheck size={20} />, color: "#3B82F6" }
                ].map(({ label, value, icon, color }) => (
                  <div key={label} style={{ ...cardStyle, textAlign: "center", padding: 20, marginBottom: 0 }}>
                    <div style={{ color, marginBottom: 8 }}>{icon}</div>
                    <div style={{ fontSize: 24, fontWeight: 800, color: "#E2E8F0" }}>{value}</div>
                    <div style={{ fontSize: 11, color: "#64748B", marginTop: 4 }}>{label}</div>
                  </div>
                ));
              })()}
            </div>
          )}

          {/* Employee self-mark */}
          {mode === "employee" && currentEmp && (
            <div style={cardStyle}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#E2E8F0", marginBottom: 16 }}>
                <Clock size={16} style={{ verticalAlign: "middle", marginRight: 8 }} />
                Today's Attendance — {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
              </div>
              {getTodayStatus(getEmpId(currentEmp)) ? (
                <div style={{ padding: 16, borderRadius: 10, background: "rgba(16,185,129,.06)", border: "1px solid rgba(16,185,129,.15)", display: "flex", alignItems: "center", gap: 12 }}>
                  <CheckCircle size={20} color="#10B981" />
                  <span style={{ color: "#E2E8F0", fontSize: 14, fontWeight: 600 }}>
                    Marked as <span style={{ color: "#10B981", textTransform: "capitalize" }}>{getTodayStatus(getEmpId(currentEmp))}</span>
                  </span>
                </div>
              ) : (
                <div style={{ display: "flex", gap: 12 }}>
                  <button onClick={() => markAttendance(getEmpId(currentEmp), "present")} style={{ flex: 1, padding: "14px 0", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#10B981,#059669)", color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>✓ Present</button>
                  <button onClick={() => markAttendance(getEmpId(currentEmp), "halfday")} style={{ flex: 1, padding: "14px 0", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#F59E0B,#D97706)", color: "#000", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>☕ Half Day</button>
                  <button onClick={() => markAttendance(getEmpId(currentEmp), "absent")} style={{ flex: 1, padding: "14px 0", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#EF4444,#DC2626)", color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>✗ Absent</button>
                </div>
              )}
            </div>
          )}

          {/* Admin — All employees attendance */}
          {mode === "admin" && (
            <div style={cardStyle}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#E2E8F0", marginBottom: 16 }}>
                Team Attendance — {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {["Employee", "Code", "Today", "Present", "Absent", "Half Day", "Attendance %", "Action"].map(h => (
                        <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: 0.5, borderBottom: "1px solid rgba(100,116,139,.15)" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {employees.map(emp => {
                      const empId = getEmpId(emp);
                      const stats = getStats(empId);
                      const todayStatus = getTodayStatus(empId);
                      return (
                        <tr key={empId} style={{ borderBottom: "1px solid rgba(100,116,139,.08)" }}>
                          <td style={{ padding: "12px", fontSize: 13, fontWeight: 600, color: "#E2E8F0" }}>{emp.name}</td>
                          <td style={{ padding: "12px", fontSize: 12, color: "#94A3B8" }}>{emp.code || emp.employeeCode || "N/A"}</td>
                          <td style={{ padding: "12px" }}>
                            {todayStatus ? (
                              <span style={badgeStyle(todayStatus === "present" ? "16,185,129" : todayStatus === "absent" ? "239,68,68" : "245,158,11")}>
                                {todayStatus === "present" ? "✓ Present" : todayStatus === "absent" ? "✗ Absent" : "☕ Half"}
                              </span>
                            ) : <span style={{ fontSize: 12, color: "#475569" }}>Not marked</span>}
                          </td>
                          <td style={{ padding: "12px", fontSize: 13, fontWeight: 600, color: "#10B981" }}>{stats.present}</td>
                          <td style={{ padding: "12px", fontSize: 13, fontWeight: 600, color: "#EF4444" }}>{stats.absent}</td>
                          <td style={{ padding: "12px", fontSize: 13, fontWeight: 600, color: "#F59E0B" }}>{stats.halfDay}</td>
                          <td style={{ padding: "12px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <div style={{ flex: 1, height: 6, borderRadius: 3, background: "rgba(100,116,139,.15)", overflow: "hidden" }}>
                                <div style={{ width: stats.percentage + "%", height: "100%", borderRadius: 3, background: stats.percentage >= 80 ? "#10B981" : stats.percentage >= 60 ? "#F59E0B" : "#EF4444" }} />
                              </div>
                              <span style={{ fontSize: 12, fontWeight: 700, color: "#E2E8F0", minWidth: 35 }}>{stats.percentage}%</span>
                            </div>
                          </td>
                          <td style={{ padding: "12px" }}>
                            <div style={{ display: "flex", gap: 6 }}>
                                <button onClick={() => markAttendance(empId, "present")} style={{ padding: "4px 10px", borderRadius: 6, border: todayStatus === "present" ? "2px solid #10B981" : "none", background: "rgba(16,185,129,.12)", color: "#10B981", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>P</button>
                                <button onClick={() => markAttendance(empId, "halfday")} style={{ padding: "4px 10px", borderRadius: 6, border: todayStatus === "halfday" ? "2px solid #F59E0B" : "none", background: "rgba(245,158,11,.12)", color: "#F59E0B", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>H</button>
                                <button onClick={() => markAttendance(empId, "absent")} style={{ padding: "4px 10px", borderRadius: 6, border: todayStatus === "absent" ? "2px solid #EF4444" : "none", background: "rgba(239,68,68,.12)", color: "#EF4444", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>A</button>
                              </div>
                          </td>
                        </tr>
                      );
                    })}
                    {employees.length === 0 && (
                      <tr><td colSpan={8} style={{ padding: 40, textAlign: "center", color: "#475569", fontSize: 13 }}>No employees found. Add team members first.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* ====== LEAVE REQUESTS TAB ====== */}
      {tab === "leaves" && (
        <>
          {/* Leave Form Modal */}
          {showLeaveForm && (
            <div style={cardStyle}>
              <h4 style={{ fontSize: 15, fontWeight: 700, color: "#E2E8F0", marginBottom: 16 }}>Apply for Leave</h4>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: "#64748B", display: "block", marginBottom: 4 }}>Leave Type</label>
                  <select value={leaveForm.type} onChange={e => setLeaveForm({ ...leaveForm, type: e.target.value })} style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid rgba(100,116,139,.25)", background: "rgba(15,23,42,.8)", color: "#E2E8F0", fontSize: 13, outline: "none" }}>
                    {["Sick Leave", "Casual Leave", "Earned Leave", "Personal Leave", "Emergency Leave"].map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div />
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: "#64748B", display: "block", marginBottom: 4 }}>From Date</label>
                  <input type="date" value={leaveForm.from} onChange={e => setLeaveForm({ ...leaveForm, from: e.target.value })} style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid rgba(100,116,139,.25)", background: "rgba(15,23,42,.8)", color: "#E2E8F0", fontSize: 13, outline: "none", boxSizing: "border-box" }} />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: "#64748B", display: "block", marginBottom: 4 }}>To Date</label>
                  <input type="date" value={leaveForm.to} onChange={e => setLeaveForm({ ...leaveForm, to: e.target.value })} style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid rgba(100,116,139,.25)", background: "rgba(15,23,42,.8)", color: "#E2E8F0", fontSize: 13, outline: "none", boxSizing: "border-box" }} />
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: "#64748B", display: "block", marginBottom: 4 }}>Reason</label>
                  <textarea value={leaveForm.reason} onChange={e => setLeaveForm({ ...leaveForm, reason: e.target.value })} placeholder="Enter reason for leave..." rows={3} style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid rgba(100,116,139,.25)", background: "rgba(15,23,42,.8)", color: "#E2E8F0", fontSize: 13, outline: "none", resize: "vertical", boxSizing: "border-box" }} />
                </div>
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
                <button onClick={submitLeave} style={{ padding: "10px 24px", borderRadius: 8, border: "none", background: "linear-gradient(135deg,#3B82F6,#2563EB)", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Submit Leave Request</button>
                <button onClick={() => setShowLeaveForm(false)} style={{ padding: "10px 24px", borderRadius: 8, border: "1px solid rgba(100,116,139,.3)", background: "transparent", color: "#94A3B8", fontSize: 13, cursor: "pointer" }}>Cancel</button>
              </div>
            </div>
          )}

          {/* Leave List */}
          <div style={cardStyle}>
            <h4 style={{ fontSize: 14, fontWeight: 700, color: "#E2E8F0", marginBottom: 16 }}>
              {mode === "admin" ? "All Leave Requests" : "My Leave Requests"}
            </h4>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {[...(mode === "admin" ? ["Employee"] : []), "Type", "From", "To", "Days", "Reason", "Status", ...(mode === "admin" ? ["Action"] : [])].map(h => (
                    <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#64748B", textTransform: "uppercase", borderBottom: "1px solid rgba(100,116,139,.15)" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {leaves
                  .filter(l => mode === "admin" ? true : (l.empId === (currentEmp ? getEmpId(currentEmp) : currentUserId)))
                  .sort((a, b) => new Date(b.appliedOn) - new Date(a.appliedOn))
                  .map(leave => (
                    <tr key={leave.id} style={{ borderBottom: "1px solid rgba(100,116,139,.08)" }}>
                      {mode === "admin" && <td style={{ padding: "12px", fontSize: 13, fontWeight: 600, color: "#E2E8F0" }}>{leave.empName}</td>}
                      <td style={{ padding: "12px", fontSize: 12, color: "#94A3B8" }}>{leave.type}</td>
                      <td style={{ padding: "12px", fontSize: 12, color: "#94A3B8" }}>{leave.from}</td>
                      <td style={{ padding: "12px", fontSize: 12, color: "#94A3B8" }}>{leave.to}</td>
                      <td style={{ padding: "12px", fontSize: 12, fontWeight: 700, color: "#E2E8F0" }}>{daysBetween(leave.from, leave.to)}</td>
                      <td style={{ padding: "12px", fontSize: 12, color: "#94A3B8", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{leave.reason}</td>
                      <td style={{ padding: "12px" }}>
                        <span style={badgeStyle(leave.status === "Approved" ? "16,185,129" : leave.status === "Rejected" ? "239,68,68" : "245,158,11")}>
                          {leave.status}
                        </span>
                      </td>
                      {mode === "admin" && (
                        <td style={{ padding: "12px" }}>
                          {leave.status === "Pending" ? (
                            <div style={{ display: "flex", gap: 6 }}>
                              <button onClick={() => updateLeave(leave.id, "Approved")} style={{ padding: "4px 12px", borderRadius: 6, border: "none", background: "rgba(16,185,129,.12)", color: "#10B981", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>Approve</button>
                              <button onClick={() => updateLeave(leave.id, "Rejected")} style={{ padding: "4px 12px", borderRadius: 6, border: "none", background: "rgba(239,68,68,.12)", color: "#EF4444", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>Reject</button>
                            </div>
                          ) : <span style={{ fontSize: 11, color: "#475569" }}>—</span>}
                        </td>
                      )}
                    </tr>
                  ))}
                {leaves.filter(l => mode === "admin" ? true : (l.empId === (currentEmp ? getEmpId(currentEmp) : currentUserId))).length === 0 && (
                  <tr><td colSpan={mode === "admin" ? 8 : 7} style={{ padding: 40, textAlign: "center", color: "#475569", fontSize: 13 }}>No leave requests yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
